// Public marketing landing page (NO auth). Logged-in users are redirected
// to / (dashboard) by App.jsx. Anonymous visitors see this with the hero,
// three value-prop tiles, and footer links to ToS / Privacy / FAQ.
//
// Per-facility customization:
//   * If the URL has ?fac=FAC001 (or /landing/FAC001 if you wire that later),
//     the backend returns that facility's branding + hero copy + name.
//   * Otherwise we render the platform default.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import Hero from '../components/Hero.jsx';

export default function Landing() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const code = params.get('fac') || params.get('code') || null;

  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.publicLanding(code)
      .then(r => { if (alive) setData(r.landing); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [code]);

  const b = data || {
    display_name: 'Meds-to-Beds',
    theme_primary: '#1d4ed8',
    theme_accent:  '#0ea5e9',
    hero_tagline: 'Your medications. Delivered to your bedside.',
    hero_subtext: 'Skip the pharmacy stop on the way home. Your prescriptions are reviewed, counseled on, and brought to your room before discharge.',
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top brand bar */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {b.logo_url
              ? <img src={b.logo_url} alt="" className="h-8 w-8 rounded" />
              : <div className="h-8 w-8 rounded" style={{ background: b.theme_primary }} />
            }
            <span className="font-semibold text-slate-800">{b.display_name || 'Meds-to-Beds'}</span>
          </div>
          <nav className="flex gap-4 text-sm text-slate-600">
            <Link to="/help/faq" className="hover:text-slate-900">FAQ</Link>
            <Link to="/legal/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/login" className="font-medium" style={{ color: b.theme_primary }}>Sign in</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <Hero
        tagline={b.hero_tagline}
        subtext={b.hero_subtext}
        facilityName={b.display_name}
        primary={b.theme_primary}
        accent={b.theme_accent}
        ctaLabel="Staff sign in"
        onCta={() => nav('/login')}
        secondaryLabel="Read FAQ"
        onSecondary={() => nav('/help/faq')}
      />

      {/* Value props */}
      <section className="max-w-6xl mx-auto px-6 py-12 grid sm:grid-cols-3 gap-6">
        <ValueCard
          color={b.theme_primary}
          icon={<IconBedside />}
          title="Bedside fulfillment"
          body="Your prescriptions are reviewed by a pharmacist, filled, and brought to your room before you're discharged. No pharmacy stop on the way home."
        />
        <ValueCard
          color={b.theme_primary}
          icon={<IconCounsel />}
          title="Counseling included"
          body="A pharmacy team member walks you through dosing, side effects, and interactions face-to-face — or by phone for refills."
        />
        <ValueCard
          color={b.theme_primary}
          icon={<IconShield />}
          title="Pay securely, on your terms"
          body="Pay on the device the nurse hands you, or get a secure pay link by text message and pay later from your phone."
        />
      </section>

      {/* Footer */}
      <footer className="mt-auto border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-500 justify-between">
          <div>© {new Date().getFullYear()} {b.display_name || 'Meds-to-Beds'}. For demo / educational use.</div>
          <div className="flex gap-4">
            <Link to="/legal/terms"   className="hover:text-slate-700">Terms of Use</Link>
            <Link to="/legal/privacy" className="hover:text-slate-700">Privacy Notice</Link>
            <Link to="/help/faq"      className="hover:text-slate-700">FAQ &amp; Help</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function ValueCard({ color, icon, title, body }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{ background: `${color}15`, color }}>
        {icon}
      </div>
      <div className="font-semibold text-slate-800">{title}</div>
      <p className="text-sm text-slate-600 mt-1 leading-relaxed">{body}</p>
    </div>
  );
}

function IconBedside() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 14h18v6H3z" /><path d="M5 14V8h6v6" /><path d="M21 14v-2a4 4 0 0 0-4-4h-2" />
    </svg>
  );
}
function IconCounsel() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}
function IconShield() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" />
    </svg>
  );
}
